import { useMemo, useState } from 'react';
import { Bar, BarChart, ComposedChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import styled from 'styled-components';
import { useDeviceTelemetry } from '../../hooks/useDeviceTelemetry';
import { Device } from '../../models/Device';
import { GraphContainer, GraphDescription, GraphTitle, TimeRangeSelectorStyled } from './GraphParts';
import { DeviceMileageBarChart, Granularity } from './DeviceMileageBarChart';
import { DeviceGsmSignalChart } from './DeviceGsmSignalChart';

const Root = styled.div`
  display: block;
  width: 100%;
`;

// Contains a number of graphs
const Graphs = styled.div`
  display: flex;
  flex-direction: column;
`;

/**
 * 
 * @param param0 
 * @returns 
 */
function TimeRangeSelector({ currentSelection, onSelect }: { currentSelection: number, onSelect: (selection: number) => void }) {
  return (
    <div>
      <button disabled={currentSelection === 1} onClick={() => onSelect(1)}>1 Day</button>
      <button disabled={currentSelection === 3} onClick={() => onSelect(3)}>3 Day</button>
      <button disabled={currentSelection === 7} onClick={() => onSelect(7)}>7 Day</button>
    </div>
  );
}

export function DeviceDiagnostics({ device }: { device: Device }) {
  const imei = device.imei;
  const [durationDays, setDurationDays] = useState(1);
  const { data, isLoading, isError, rawDataUrl, window } = useDeviceTelemetry({ imei, durationDays });
  const buckets = data?.buckets || [];
  const mileageGranularity = useMemo(() => durationDays >= 3 ? Granularity.DAYS : Granularity.HOURS, [durationDays])

  if (isError) {
    return <p>Failed to load data</p>;
  }

  return (
    <Root className={isLoading ? "loadingdiv" : ""}>
      <TimeRangeSelectorStyled style={{ textAlign: 'center' }}>
        <a target="_blank" rel="noreferrer" href={rawDataUrl}>Raw Data</a>
        <TimeRangeSelector currentSelection={durationDays} onSelect={(days) => setDurationDays(days)} />
      </TimeRangeSelectorStyled>
      <Graphs>
        <GraphContainer>
          <GraphTitle>Uptime</GraphTitle>
          <ResponsiveContainer width="100%" height={80}>
            <ComposedChart data={buckets}>
              <Bar dataKey="packetCount" fill="#8884d8" />
              <XAxis dataKey="key" hide={true} />
              <Tooltip/>
            </ComposedChart>
          </ResponsiveContainer>
          <GraphDescription>
            Displays overall uptime / availability of a device. Each bar represents a 15 minute bucket. If no bar is present then the device was not connected during this time.
          </GraphDescription>
        </GraphContainer>

        <DeviceGsmSignalChart buckets={buckets} make={device.make} isLoading={isLoading} />

        <DeviceMileageBarChart
          imei={device.imei}
          startAt={window.startAt}
          endAt={window.endAt}
          granularity={mileageGranularity}
          timezone='America/Los_Angeles' />

        <GraphContainer>
          <GraphTitle>Packet Lag</GraphTitle>
          <ResponsiveContainer width="100%" height={80}>
            <BarChart data={buckets}>
              <Bar dataKey="averageLagMs" fill="#8884d8" />
              <XAxis dataKey="key" hide={true} />
              <Tooltip/>
              <ReferenceLine y={3000} label="" stroke="orange" />
              <YAxis hide={true} type="number" domain={[0, 3000]} />
            </BarChart>
          </ResponsiveContainer>
          <GraphDescription>
            Displays the average packet lag for location packets only. Each bar represents a 15 minute bucket.
          </GraphDescription>
        </GraphContainer>

        <GraphContainer>
          <GraphTitle>Out of Order Packets</GraphTitle>
          <ResponsiveContainer width="100%" height={80}>
            <BarChart data={buckets}>
              <Bar dataKey="outOfOrderCount" fill="#8884d8" />
              <XAxis dataKey="key" hide={true} />
              <Tooltip/>
            </BarChart>
          </ResponsiveContainer>
          <GraphDescription>
            If a packet is received out of order then bars are displayed here. Normal operation should have no out of order packets.
          </GraphDescription>
        </GraphContainer>


        <GraphContainer>
          <GraphTitle>Position Packet Count</GraphTitle>
          <ResponsiveContainer width="100%" height={80}>
            <BarChart data={buckets}>
              <Bar dataKey="positionPacketCount" fill="#568a3e" />
              <XAxis dataKey="key" hide={true} />
              <Tooltip/>
            </BarChart>
          </ResponsiveContainer>
          <GraphDescription>
            Position packets differ to other other packets (e.g heartbeats) because they contain lat/lng positions. They are emitted more frequently when a device is moving.
          </GraphDescription>
        </GraphContainer>

        <GraphContainer>
          <GraphTitle>Large Gap Count</GraphTitle>
          <ResponsiveContainer width="100%" height={80}>
            <BarChart data={buckets}>
              <Bar dataKey="largePositionGapCount" fill="#ff0000" />
              <XAxis dataKey="key" hide={true} />
              <Tooltip/>
            </BarChart>
          </ResponsiveContainer>
          <GraphDescription>
            Tracks the number of times we receive a new lat/lng that is &gt; 500 meters away from the previous lat/lng. The fewer occurences the better.
          </GraphDescription>
        </GraphContainer>

        { ['AT1', 'ATA'].includes(device.model) ? (
          <GraphContainer>
            <GraphTitle>Battery</GraphTitle>
            <ResponsiveContainer width="100%" height={80}>
              <BarChart data={buckets}>
                <Bar dataKey="batteryPercent" fill="#8884d8" />
                <YAxis dataKey="batteryPercent" hide={true} domain={[0, 100]} />
                <XAxis dataKey="key" hide={true} />
                <Tooltip/>
              </BarChart>
            </ResponsiveContainer>
        </GraphContainer>
        ) : null}

      </Graphs>
    </Root>
  );
};
